/**
 * Blog post registry. Import all posts and re-export as a sorted, filtered list.
 * Posts are only visible when their publishDate <= today.
 */

export interface BlogPost {
  slug: string;
  title: string;
  description: string;
  date: string;
  readTime: string;
  category: string;
  publishDate: string; // ISO date, e.g. '2026-03-03'
  internalLinks: string[];
  content: string;
}

// Import all blog post content files
// Legacy posts (hardcoded components) are listed here for the blog index
const legacyPosts: Omit<BlogPost, 'content' | 'internalLinks'>[] = [
  {
    slug: 'ai-accountability-vs-human-coach',
    title: 'AI Accountability Coach vs Human Coach: Which Is Right for You?',
    description: 'Compare AI-powered accountability tools like Ask Aspen with traditional human coaches. Learn the pros, cons, and ideal use cases for each approach.',
    date: 'March 1, 2026',
    readTime: '6 min read',
    category: 'Productivity',
    publishDate: '2026-03-01',
  },
  {
    slug: 'adhd-task-management-ai',
    title: 'ADHD Task Management: How AI Text Coaching Helps You Stay on Track',
    description: 'Discover how AI-powered text-based task management can help people with ADHD build external structure, reduce overwhelm, and actually complete tasks.',
    date: 'March 1, 2026',
    readTime: '7 min read',
    category: 'ADHD',
    publishDate: '2026-03-01',
  },
];

// New content-driven posts — dynamically imported
const postModules = import.meta.glob<{ post: BlogPost }>('./[0-9]*.ts', { eager: true });

const contentPosts: BlogPost[] = Object.values(postModules)
  .filter((m) => m.post && m.post.slug)
  .map((m) => m.post);

/**
 * Returns all published blog posts (publishDate <= today), sorted newest first.
 * Legacy posts (with dedicated components) are included in the listing
 * but rendered by their own route components.
 */
export function getPublishedPosts(): (BlogPost | Omit<BlogPost, 'content' | 'internalLinks'>)[] {
  const today = new Date().toISOString().split('T')[0];
  const allPosts = [
    ...legacyPosts.map((p) => ({ ...p, _legacy: true })),
    ...contentPosts.filter((p) => p.publishDate <= today),
  ];
  // Sort newest first by publishDate/date
  allPosts.sort((a, b) => {
    const da = 'publishDate' in a ? a.publishDate : '2026-01-01';
    const db = 'publishDate' in b ? b.publishDate : '2026-01-01';
    return db.localeCompare(da);
  });
  return allPosts;
}

/**
 * Returns ALL content-driven posts (for route registration).
 * This includes future posts so routes exist but they won't appear in listings.
 */
export function getAllContentPosts(): BlogPost[] {
  return contentPosts;
}

/**
 * Returns a single content post by slug, or undefined.
 */
export function getPostBySlug(slug: string): BlogPost | undefined {
  return contentPosts.find((p) => p.slug === slug);
}
